import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
    CONCEPT_CATEGORIES, KIND_LABEL, SCOPE_LEVEL_LABEL, categoryForConceptType, conceptHref, displayName, documentHref,
    formatValue, parameterHref, policyHref, scopeLevelColor,
} from '../Ontology/ontologyFormat';
import { Governance, GovernanceConcept, GovernanceParameter } from './explainabilityTypes';
import { dsVar, ink } from '../../theme/tokens';

/**
 * What governed a stage: the policies behind it, the thresholds they set, the
 * Risk Library entries its configuration evaluates (grouped by category), and
 * the documents all of them cite.
 *
 * Each code links to its ontology page in a new tab, so following one doesn't
 * lose the stage the reader was in. Styled with sx: it renders inside a portalled drawer.
 */

const subheading = { fontSize: '0.75rem', fontWeight: 600, color: dsVar('fg-soft'), mb: 0.75 };
const row = { border: `1px solid ${dsVar('line')}`, borderRadius: '8px', px: 1.25, py: 0.875 };
const muted = { fontSize: '0.6875rem', color: dsVar('fg-muted') };
const pill = { fontSize: '0.625rem', fontWeight: 600, borderRadius: '4px', px: 0.75, py: 0.125, whiteSpace: 'nowrap' as const };

/** A code or name that links to its ontology page when it has one. */
const OntologyLink: React.FC<{ href: string | null; children: React.ReactNode; mono?: boolean }> = ({ href, children, mono }) => {
    const sx = { fontSize: mono ? '0.75rem' : '0.8125rem', fontWeight: 600, fontFamily: mono ? 'monospace' : undefined, color: href ? ink('#7DD3FC') : dsVar('fg-body') };
    return href ? (
        <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={sx}>{children}</Link>
    ) : (
        <Box component="span" sx={sx}>{children}</Box>
    );
};

const ScopePill: React.FC<{ level?: string }> = ({ level }) => (
    level ? (
        <Box component="span" sx={{ ...pill, color: ink(scopeLevelColor(level)), border: `1px solid ${scopeLevelColor(level)}55` }}>
            {SCOPE_LEVEL_LABEL[level] ?? level}
        </Box>
    ) : null
);

const PolicyCodes: React.FC<{ codes: string[]; label: string }> = ({ codes, label }) => (
    <Typography component="div" sx={{ ...muted, mt: 0.25 }}>
        {label}{' '}
        {codes.map((code, i) => (
            <React.Fragment key={code}>
                {i > 0 && ', '}
                <OntologyLink href={policyHref(code)} mono>{code}</OntologyLink>
            </React.Fragment>
        ))}
    </Typography>
);

/** A threshold reads better by the concept it sets than by its parameter code. */
const parameterName = (parameter: GovernanceParameter, concepts: GovernanceConcept[]): string => {
    const concept = concepts.find((c) => c.concept_code && c.concept_code === parameter.concept_code);
    return displayName(concept?.preferred_label, parameter.parameter_code);
};

/**
 * What a step DID with each Risk Library category, keyed by CONCEPT_CATEGORIES id.
 *
 * Not one "Concepts evaluated" heading: as in the library itself, there is no umbrella
 * term that reads correctly for a red flag, a control and a case status at once. The
 * nouns match the library's category names so a reader recognises them on click-through.
 */
const CATEGORY_HEADING: Record<string, string> = {
    red_flags: 'Red flags checked',
    typologies: 'Typologies considered',
    controls: 'Detection controls run',
    measures: 'Measures computed',
    case_statuses: 'Case statuses referenced',
    screening: 'Screening categories checked',
    ratings: 'Ratings & priorities applied',
    entities: 'Customer & entity terms referenced',
};

/** Entries whose type no category claims (or that carry no type) — not dropped, just unfiled. */
const OTHER_HEADING = `Other ${KIND_LABEL.concepts} entries`;

/** Concepts grouped under their category headings, in the library's rail order; unfiled last. */
const groupConcepts = (concepts: GovernanceConcept[]): { heading: string; concepts: GovernanceConcept[] }[] => {
    const groups = CONCEPT_CATEGORIES.map((category) => ({
        heading: CATEGORY_HEADING[category.id] ?? category.label,
        concepts: concepts.filter((c) => categoryForConceptType(c.concept_type)?.id === category.id),
    }));
    groups.push({ heading: OTHER_HEADING, concepts: concepts.filter((c) => !categoryForConceptType(c.concept_type)) });
    return groups.filter((group) => group.concepts.length > 0);
};

const differs = (a: unknown, b: unknown) => a !== undefined && b !== undefined && String(a) !== String(b);

/** `SYNBAN.TM.structuring §3`: the citing code with the pinpoint it cites. */
const citerLabel = (citer: { kind: string; code: string; pinpoint?: string }) =>
    citer.pinpoint ? `${citer.code} ${citer.pinpoint}` : citer.code;

const GovernanceSection: React.FC<{ governance: Governance }> = ({ governance }) => {
    const policies = governance.policies ?? [];
    const parameters = governance.parameters ?? [];
    const concepts = governance.concepts ?? [];
    const sources = governance.sources ?? [];
    const unresolved = Object.values(governance.unresolved ?? {}).flat();

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {policies.length > 0 && (
                <Box>
                    <Typography sx={subheading}>Policies</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {policies.map((policy) => (
                            <Box key={policy.policy_code} sx={row}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <OntologyLink href={policyHref(policy.policy_code)}>
                                            {policy.title || policy.policy_code}
                                        </OntologyLink>
                                        {policy.section && <Box component="span" sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}> §{policy.section}</Box>}
                                    </Box>
                                    <ScopePill level={policy.scope_level} />
                                </Box>
                                <Typography sx={muted}>
                                    {[policy.title ? policy.policy_code : null, policy.regulator, policy.jurisdiction].filter(Boolean).join(' · ')}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {parameters.length > 0 && (
                <Box>
                    <Typography sx={subheading}>Thresholds applied</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {parameters.map((parameter) => {
                            const applied = parameter.applied_value ?? parameter.value;
                            return (
                                <Box key={parameter.parameter_code} sx={row}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                                        <OntologyLink href={parameterHref(parameter.parameter_code)}>
                                            {parameterName(parameter, concepts)}
                                        </OntologyLink>
                                        <Typography component="span" sx={{ fontSize: '0.8125rem', color: dsVar('fg'), fontWeight: 600, whiteSpace: 'nowrap' }}>
                                            {formatValue(applied, parameter.unit)}
                                        </Typography>
                                    </Box>
                                    {differs(parameter.applied_value, parameter.value) && (
                                        <Typography sx={{ fontSize: '0.6875rem', color: ink('#FCD34D') }}>
                                            The policy sets {formatValue(parameter.value, parameter.unit)}; this run applied {formatValue(parameter.applied_value, parameter.unit)}
                                        </Typography>
                                    )}
                                    {parameter.policy_codes?.length ? (
                                        <PolicyCodes codes={parameter.policy_codes} label="Set by" />
                                    ) : (
                                        <Typography sx={{ ...muted, mt: 0.25 }}>Not set by a policy</Typography>
                                    )}
                                    {parameter.config_keys?.length ? (
                                        <Typography sx={{ ...muted, fontFamily: '"JetBrains Mono", monospace' }}>{parameter.config_keys.join(', ')}</Typography>
                                    ) : null}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {groupConcepts(concepts).map((group) => (
                <Box key={group.heading}>
                    <Typography sx={subheading}>{group.heading}</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {group.concepts.map((concept) => (
                            <Box key={concept.concept_code} sx={row}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                                    <OntologyLink href={conceptHref(concept.concept_code)}>
                                        {displayName(concept.preferred_label, concept.concept_code)}
                                    </OntologyLink>
                                    {concept.value_source && (
                                        <Box
                                            component="span"
                                            sx={{ ...pill, ...(concept.value_source === 'policy'
                                                ? { color: '#34D399', border: '1px solid rgba(52, 211, 153, 0.3)' }
                                                : { color: dsVar('fg-2'), border: `1px solid ${dsVar('line')}` }) }}
                                        >
                                            {concept.value_source === 'policy' ? 'Value from policy' : 'Value from configuration'}
                                        </Box>
                                    )}
                                </Box>
                                {concept.definition && (
                                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), lineHeight: 1.5, mt: 0.25 }}>{concept.definition}</Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            ))}

            {sources.length > 0 && (
                <Box>
                    <Typography sx={subheading}>Sources cited</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {sources.map((source, i) => (
                            <Box key={source.source_doc_code ?? i} sx={row}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                                    <OntologyLink href={documentHref(source.source_doc_code) ?? source.source_link ?? null}>
                                        {source.title || source.source_doc_code}
                                    </OntologyLink>
                                    {source.provisional && (
                                        <Box component="span" sx={{ ...pill, color: ink('#FCD34D'), border: '1px solid rgba(251, 191, 36, 0.3)' }}>Provisional</Box>
                                    )}
                                </Box>
                                <Typography sx={muted}>{[source.issuer, source.citation].filter(Boolean).join(' · ')}</Typography>
                                {source.cited_by?.length ? (
                                    <Typography sx={{ ...muted, mt: 0.25 }}>Cited by {source.cited_by.map(citerLabel).join(', ')}</Typography>
                                ) : null}
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {unresolved.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', p: 1, borderRadius: '8px', border: '1px solid rgba(251, 191, 36, 0.3)', backgroundColor: 'rgba(251, 191, 36, 0.06)' }}>
                    <WarningAmberIcon sx={{ fontSize: 16, color: ink('#FBBF24'), mt: '1px' }} />
                    <Typography sx={{ fontSize: '0.75rem', color: ink('#FCD34D') }}>
                        This step names {unresolved.length === 1 ? 'a code' : 'codes'} the ontology doesn't hold, so {unresolved.length === 1 ? 'its definition and citations are' : 'their definitions and citations are'} missing:{' '}
                        <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>{unresolved.join(', ')}</Box>
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default GovernanceSection;
